import { useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from "./axios";

export type WarrantyClaimApi = {
  claimId: number;
  claimNumber: string;
  claimDate: string;
  status: string;
  claimMode?: "REFUND" | "REPLACEMENT";
  paymentMode?: string;
  refNo?: string;
  roundedAmount?: number;
  issueDescription: string;
  resolutionNotes: string;
  attachment: string | null;
};

export type WarrantyProductApi = {
  salesProductId: number;
  productId: number;
  productName: string;
  brandName: string;
  productImageUrl: string | null;
  sellingPrice: number;
  mrp: number | null;
  warrantyStatus: string;
  warrantyExpiryDate: string;
  claims: WarrantyClaimApi[];
  claimedQuantity: number;
  availableForClaim: number;
};

export type WarrantyListItemApi = {
  salesId: number;
  invoiceNumber: string;
  customerName: string;
  purchaseDate: string;
  products?: WarrantyProductApi[];
};

export type WarrantyListResponse = {
  content: WarrantyListItemApi[];
  totalPages: number;
  totalElements: number;
  pageNumber: number;
};

export type WarrantyDetailResponse = {
  salesId: number;
  invoiceNumber: string;
  customerName: string;
  purchaseDate: string;
  products: WarrantyProductApi[];
};

export type CreateWarrantyClaimPayload = {
  salesProductId: number;
  salesId: number;
  claimDate: string;
  claimQuantity: number;
  claimMode: "REFUND" | "REPLACEMENT";
  status?: string;
  paymentMode?: string;
  refNo?: string;
  roundedAmount?: number;
  issueDescription: string;
  attachment: string;
};

export type UpdateWarrantyClaimPayload = {
  claimId: number;
  claimNumber: string;
  claimDate: string;
  status: string;
  claimMode: "REFUND" | "REPLACEMENT";
  paymentMode?: string;
  refNo?: string;
  roundedAmount?: number;
  issueDescription: string;
  resolutionNotes: string;
  attachment: string;
};

export const useGetAllWarranties = (
  size: number,
  page: number,
  invoiceNumber?: string,
  status?: string,
) => {
  return useQuery({
    queryKey: ["useGetAllWarranties", size, page, invoiceNumber, status],
    queryFn: async () => {
      const { data } = await axiosInstance.get<WarrantyListResponse>("/warranty/all", {
        params: {
          size,
          page,
          invoiceNumber,
          status,
        },
      });
      return data;
    },
  });
}

export const useGetWarrantyDetail = (salesId?: string) => {
  return useQuery({
    queryKey: ["useGetWarrantyDetail", salesId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<WarrantyDetailResponse>(`/warranty/${salesId}`);
      return data;
    },
    enabled: !!salesId,
  });
};

export const useGetWarrantyClaim = (claimId?: string) => {
  return useQuery({
    queryKey: ["useGetWarrantyClaim", claimId],
    queryFn: async () => {
      const { data } = await axiosInstance.get<WarrantyClaimApi>(`/warranty/claim/${claimId}`);
      return data;
    },
    enabled: !!claimId,
  });
};

export const useCreateWarrantyClaim = () => {
  return useMutation({
    mutationFn: async (payload: CreateWarrantyClaimPayload) => {
      const { data } = await axiosInstance.post("/warranty/claim", payload);
      return data;
    },
  });
};

export const useUpdateWarrantyClaim = (claimId?: string) => {
  return useMutation({
    mutationFn: async (payload: UpdateWarrantyClaimPayload) => {
      const { data } = await axiosInstance.put(
        `/warranty/claim/${claimId}`,
        payload,
      );
      return data;
    },
  });
};

export const useDownloadWarrantyInvoicesExcel = () => {
  return useMutation({
    mutationFn: async (params: { invoiceNumber?: string; status?: string }) => {
      const response = await axiosInstance.get("/warranty/all/export/excel", {
        params,
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `warranty-invoices.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadDamagedProductsExcel = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const response = await axiosInstance.get("/warranty/damage-products/all/export/excel", {
        params,
        responseType: "blob",
      });
      return response.data;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `damaged-products.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};
